// src/rms/pages/SignedAgreement.jsx
// ============================================================================
// /signed/:token — the customer's permanent copy
// ============================================================================
// Read-only, no expiry, no account.
//
// "Retention" is one of the four things ESIGN and UETA actually ask for, and a
// copy that stops working in ninety days does not meet it. So this token has no
// expiry, and the function behind it returns the document and nothing that
// could change it -- there is no write path here at all.
//
// It prints, through the same rules as the operator's own copy in
// AgreementView: same stored characters, same layout engine, so the screen and
// the paper cannot disagree about what was agreed.
// ============================================================================

import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import agreementDocumentService from "../data/agreementDocumentService";
import AgreementReader from "../components/AgreementReader";
import { Banner, formatDateTime } from "../components/ui";
import Brandmark from "../components/Brandmark";
import { useStrippedToken } from "./SignAgreement";
import i18n from "../i18n";
import "../styles/rms.css";

const t = (key, fallback, params) =>
  i18n.t(key, { defaultValue: fallback, ...(params || {}) });

const STASH = "rms.signed.token";

export default function SignedAgreement() {
  const { token: paramToken } = useParams();
  const token = useStrippedToken(paramToken, STASH);

  const [doc, setDoc] = useState(null);
  const [problem, setProblem] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const result = await agreementDocumentService.viewSigned(token);
    setLoading(false);
    if (!result.success) {
      setProblem(result.error);
      return;
    }
    setDoc(result.data);
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="rms-root rms-sign rms-printable">
      <meta name="referrer" content="no-referrer" />
      <div className="rms-sign-inner">
        <div className="rms-sign-brand rms-noprint">
          <Brandmark size="1.3em" />
        </div>

        <div className="rms-sign-card rms-print-plain">
          {loading ? (
            <p style={{ margin: 0, color: "var(--rms-muted-strong)", fontSize: 14.5 }}>
              {t("common.loading", "Loading…")}
            </p>
          ) : null}

          {problem ? <Banner tone="danger">{problem}</Banner> : null}

          {doc ? (
            <>
              <div
                className="rms-noprint"
                style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}
              >
                {doc.pdf_url ? (
                  <a
                    className="rms-btn rms-btn--ghost rms-btn--sm"
                    href={doc.pdf_url}
                    style={{ marginInlineEnd: 8 }}
                  >
                    {t("signed.downloadPdf", "Download PDF")}
                  </a>
                ) : null}
                <button
                  type="button"
                  className="rms-btn rms-btn--ghost rms-btn--sm"
                  onClick={() => window.print()}
                >
                  {t("signed.print", "Print or save as PDF")}
                </button>
              </div>

              <h1 className="rms-sign-title">{doc.title}</h1>
              <p className="rms-sign-from">
                {doc.business_name}
                {doc.signed_at ? ` · ${formatDateTime(doc.signed_at)}` : ""}
              </p>

              <AgreementReader body={doc.body} />

              <div
                style={{
                  marginTop: 26,
                  paddingTop: 16,
                  borderTop: "1px solid var(--rms-border)",
                  breakInside: "avoid",
                }}
              >
                <h2 className="rms-sign-label" style={{ fontSize: 13.5, marginBottom: 10 }}>
                  {t("signed.signature", "Signature")}
                </h2>
                <dl className="rms-agreement-audit">
                  <dt>{t("signed.signedBy", "Signed by")}</dt>
                  <dd>{doc.signer_name || "—"}</dd>

                  <dt>{t("signed.signedAt", "Signed")}</dt>
                  <dd>{formatDateTime(doc.signed_at)}</dd>

                  <dt>{t("signed.consented", "Agreed to sign electronically")}</dt>
                  <dd>{doc.consented_at ? formatDateTime(doc.consented_at) : "—"}</dd>

                  <dt>{t("signed.fingerprint", "Document fingerprint")}</dt>
                  <dd style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 11, wordBreak: "break-all" }}>
                    {doc.fingerprint}
                  </dd>
                </dl>
              </div>

              <p className="rms-sign-fine rms-noprint">
                {t(
                  "signed.keepLink",
                  "Keep this link — it opens your copy at any time and does not expire. {business} holds a copy too.",
                  { business: doc.business_name }
                )}
              </p>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
